import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getBookingDetailsById } from 'reservations/base/constants/endpoints';
import { HTTP_STATUS_CODE_403_UNAUTHORIZED } from 'reservations/base/constants/httpStatusCodes';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import is5xxError from 'reservations/base/helpers/is5xxError';
import { BookingResponseSchema } from 'reservations/booking/types';
import type {
  HandleErrorOptions,
  HandleUpdateBookingSuccessOptions,
  NextableBookingResponse
} from 'reservations/booking/types';
import { cleanUpdateBookingFormData } from 'reservations/form/helpers/cleanBookingFormData';
import { restClient } from 'utility/restClient';
import type { RequestError } from 'utility/restClient';

type UseUpdateBookingMutation = {
  slug: string;
  bookingId: string;
  apiKey: string;
  // eslint-disable-next-line unused-imports/no-unused-vars
  handleSuccess: (options: HandleUpdateBookingSuccessOptions) => void;
  // eslint-disable-next-line unused-imports/no-unused-vars
  handleError: (options: HandleErrorOptions) => void;
};

function useUpdateBookingMutation({
  slug,
  bookingId,
  apiKey,
  handleSuccess,
  handleError
}: UseUpdateBookingMutation) {
  const queryClient = useQueryClient();

  return useMutation<
    NextableBookingResponse,
    RequestError,
    Record<string, any>
  >({
    mutationFn: async formData => {
      const cleanedFormData = cleanUpdateBookingFormData(formData);
      const endpoint = getBookingDetailsById(bookingId);
      const response = await restClient.put(endpoint, {
        data: Object.fromEntries(cleanedFormData),
        nextableApiKey: apiKey
      });

      try {
        return BookingResponseSchema.parse(response);
      } catch (err) {
        console.error('useUpdateBookingMutation: ', err);
        throw err;
      }
    },
    onSuccess: data => {
      const { bookingId } = data.booking;

      queryClient.invalidateQueries({
        queryKey: ['bentoboxReservations', 'booking', bookingId]
      });

      handleSuccess({
        navUrl: `/locations/${slug}/booking/${bookingId}?modified=true`,
        booking: data.booking
      });
    },
    onError: error => {
      if (is5xxError(error.code)) {
        handleError({ navUrl: REDIRECT_WITH_ACCOUNT_DETAILS });
        return;
      }

      if (error.code === HTTP_STATUS_CODE_403_UNAUTHORIZED) {
        handleError({
          forceReload: true
        });
        return;
      }

      if (error.code === NEXTABLE_ERROR_CODE.NO_AVAILABILITY) {
        handleError({
          navUrl: `/locations/${slug}/booking/${bookingId}/modify`,
          code: error.code
        });
        return;
      }

      if (error.code === NEXTABLE_ERROR_CODE.ITEM_NOT_FOUND) {
        handleError({
          navUrl: `/locations/${slug}`,
          code: error.code
        });
        return;
      }

      if (error.code === NEXTABLE_ERROR_CODE.TIME_SLOT_UNAVAILABLE) {
        queryClient.invalidateQueries({
          queryKey: ['bentoboxReservations', 'availabilities']
        });
        handleError({
          navUrl: `/locations/${slug}/booking/${bookingId}/modify`,
          code: error.code
        });
        return;
      }

      handleError({ navUrl: `/locations/${slug}/booking/${bookingId}` });
    }
  });
}

export { useUpdateBookingMutation };
